import { questionTypes } from '../pages/ClientConnectV2/survey/SurveyPreviewBody';
import {
  getOptionId,
  isSameId,
  normalizeId,
} from '../pages/ClientConnectV2/survey/surveyPreviewUtils';
import { getStoredAnswer } from './createSurveyAnswerHandler';

const SINGLE_CHOICE_HIDE_TYPES = new Set([
  questionTypes.RADIO,
  questionTypes.RATING,
  questionTypes.EMOJI,
  questionTypes.DROPDOWN,
  questionTypes.GRIDSPOT,
]);

const MULTI_CHOICE_HIDE_TYPES = new Set([
  questionTypes.MULTI,
  questionTypes.SEQUENCE,
  questionTypes.SKILLS,
]);

const ROUTE_SKIP_TYPES = new Set([
  questionTypes.RADIO,
  questionTypes.RATING,
  questionTypes.EMOJI,
  questionTypes.DROPDOWN,
  questionTypes.GRIDSPOT,
]);

const BASE_HIDDEN_TYPES = new Set([
  'skip',
  'director_message',
]);

/**
 * Coerce hide_questions / route_to_index entries to non-negative integer indices.
 * APIs often send string digits; Number.isInteger(string) would drop them.
 */
export function toQuestionIndex(value) {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) {
    return null;
  }

  return parsed;
}

/** Normalize an array of hide/route indices (string digits → integers). */
export function normalizeQuestionIndices(indices) {
  if (!Array.isArray(indices)) {
    return [];
  }
  return indices
    .map((index) => toQuestionIndex(index))
    .filter((index) => index != null);
}

function getSelectedOptions(question, answerValue) {
  if (answerValue == null || answerValue === '') {
    return [];
  }

  const answerArr = Array.isArray(answerValue) ? answerValue : [answerValue];
  return (question.options || []).filter((opt, index) => {
    const optionId = getOptionId(opt, index);
    return answerArr.some((id) => isSameId(id, optionId));
  });
}

/** Collects hide indices from grid_hide_questions on selected grid options. */
function collectGridHideQuestions(gridAnswer, question) {
  const hideQues = [];

  if (
    !gridAnswer ||
    typeof gridAnswer !== 'object' ||
    Object.keys(gridAnswer).length === 0
  ) {
    return hideQues;
  }

  Object.keys(gridAnswer).forEach((rowId) => {
    const selectedOptionId = gridAnswer[rowId];
    const selectedOptionIds = Array.isArray(selectedOptionId)
      ? selectedOptionId
      : [selectedOptionId];

    selectedOptionIds.forEach((optionId) => {
      const selectedOption = (question?.options || []).find((option, index) =>
        isSameId(getOptionId(option, index), optionId),
      );

      if (!selectedOption?.grid_hide_questions?.length) {
        return;
      }

      const hideQuestionConfig = selectedOption.grid_hide_questions.find(
        (config) => String(config.rowId) === String(rowId),
      );

      if (hideQuestionConfig?.hide_questions?.length) {
        hideQues.push(...hideQuestionConfig.hide_questions);
      }
    });
  });

  return hideQues;
}

/**
 * Collects original-array question indices to hide for a single answer.
 * Mirrors normal survey hide_questions / grid_hide_questions behavior.
 */
export function getHideQuestionIndicesFromAnswer(question, answerValue) {
  if (answerValue == null || answerValue === '') {
    return [];
  }

  const type = question?.questionType;
  const hideIndices = [];

  // Gridspot is listed as single-choice for radio mode, but multiselect must
  // union hide_questions from every selected spot (same as API / checkbox).
  if (type === questionTypes.GRIDSPOT) {
    const selected = getSelectedOptions(question, answerValue);
    if (question?.gridInputType === questionTypes.MULTI) {
      selected.forEach((opt) => {
        if (opt?.hide_questions?.length) {
          hideIndices.push(...opt.hide_questions);
        }
      });
    } else if (selected[0]?.hide_questions?.length) {
      hideIndices.push(...selected[0].hide_questions);
    }
  } else if (SINGLE_CHOICE_HIDE_TYPES.has(type)) {
    const selected = getSelectedOptions(question, answerValue)[0];
    if (selected?.hide_questions?.length) {
      hideIndices.push(...selected.hide_questions);
    }
  } else if (MULTI_CHOICE_HIDE_TYPES.has(type)) {
    getSelectedOptions(question, answerValue).forEach((opt) => {
      if (opt?.hide_questions?.length) {
        hideIndices.push(...opt.hide_questions);
      }
    });
  } else if (type === questionTypes.GRID) {
    hideIndices.push(...collectGridHideQuestions(answerValue, question));
  }

  return hideIndices;
}

function collectHiddenQuestionIndices(allQuestions, answers) {
  const hidden = new Set();

  (allQuestions || []).forEach((question) => {
    const answer = getStoredAnswer(answers, question._id);
    getHideQuestionIndicesFromAnswer(question, answer?.value).forEach((index) => {
      const parsed = toQuestionIndex(index);
      if (parsed != null) {
        hidden.add(parsed);
      }
    });
  });

  return hidden;
}

/**
 * Indices skipped by route_to_index (questions between source and route target).
 * Same rule as normal survey contidionalAutoNext skip/grey-out.
 */
export function collectRouteSkipIndices(allQuestions, answers) {
  const skipped = new Set();

  (allQuestions || []).forEach((question, questionIndex) => {
    if (!ROUTE_SKIP_TYPES.has(question?.questionType)) {
      return;
    }

    const answer = getStoredAnswer(answers, question._id);
    const value = answer?.value;
    if (value == null || value === '') {
      return;
    }

    const selected = getSelectedOptions(question, value)[0];
    const routeToIndex = toQuestionIndex(selected?.route_to_index);
    if (routeToIndex == null) {
      return;
    }

    for (let index = questionIndex + 1; index < routeToIndex; index += 1) {
      skipped.add(index);
    }
  });

  return skipped;
}

export function isBaseVisibleSurveyQuestion(question) {
  if (!question) {
    return false;
  }
  if (BASE_HIDDEN_TYPES.has(question.questionType)) {
    return false;
  }
  if (question.skip) {
    return false;
  }
  return true;
}

/**
 * Visible questions after base type filtering + answer-driven hide/show + route skips.
 * hide_questions / route_to_index use indices into the original survey.questions array.
 *
 * @param {object} [options]
 * @param {boolean} [options.hasLocation=false] - L&D: show employees/inventory only when location is set
 *   (same as Normal survey). Client Connect keeps filtering employees in its own helpers.
 */
export function getVisibleSurveyQuestions(allQuestions, answers = {}, options = {}) {
  const { hasLocation = false, includeEmployees = hasLocation } = options;
  const questions = allQuestions || [];
  const hidden = collectHiddenQuestionIndices(questions, answers);
  const skipped = collectRouteSkipIndices(questions, answers);

  return questions.filter((question, index) => {
    if (hidden.has(index) || skipped.has(index)) {
      return false;
    }
    if (
      question.questionType === questionTypes.EMPLOYEES &&
      !includeEmployees
    ) {
      return false;
    }
    if (
      question.questionType === questionTypes.INVENTORY &&
      !hasLocation
    ) {
      return false;
    }
    return isBaseVisibleSurveyQuestion(question);
  });
}

/** Keeps the user on the same question when the visible list changes; clamps if it was hidden. */
export function resolveVisibleQuestionIndex(
  visibleQuestions,
  preferredQuestionId,
  previousIndex = 0,
) {
  if (!visibleQuestions?.length) {
    return 0;
  }

  if (preferredQuestionId != null) {
    const byId = visibleQuestions.findIndex(
      (question) => normalizeId(question._id) === normalizeId(preferredQuestionId),
    );
    if (byId !== -1) {
      return byId;
    }
  }

  return Math.min(Math.max(0, previousIndex), visibleQuestions.length - 1);
}
